import React, { useState } from "react"
import { CompletableTaskValue, InspectionTaskValue, Task, TaskValue } from "@model/Task"
import { WorkRequestType } from "@model/workRequests/WorkRequest"
interface TaskListProps {
    tasks: Task[]
    type: WorkRequestType
    onChange: (updatedTasks: Task[]) => void
}

const TaskList: React.FC<TaskListProps> = ({ tasks, type, onChange }) => {
    const [expanded, setExpanded] = useState(true)

    const handleToggle = (id: number, option: TaskValue) => {
        const updated = tasks.map((t) => {
            if (t.id !== id) return t
            return { ...t, value: option }
        })
        onChange(updated)
    }

    //check/uncheck all for a given column
    const handleCheckAll = (option: TaskValue) => {
        const updated = tasks.map((t) => ({ ...t, value: option }))
        onChange(updated)
    }

    const handleMeasurementChange = (id: number, measurement: string) => {
        const updated = tasks.map((t) =>
            t.id === id ? { ...t, measurement } : t
        )
        onChange(updated)
    }

    // Helpers to reflect "checked" state for the Check All boxes
    const allAre = (option: TaskValue) => tasks.length > 0 && tasks.every((t) => t.value === option)

    return (
        <div className="task-list-container">
            <div className="task-list__header-row">
                <h3 className="task-list__title">
                    Check Items
                    <span
                        className={`task-list__icon ${expanded ? "icon-down" : "icon-up"}`}
                        onClick={() => setExpanded((prev) => !prev)}
                        tabIndex={-1}
                    />
                </h3>
            </div>
            <div className={`task-list__content ${expanded ? "expanded" : "collapsed"}`}>
                {/* Header */}
                <div className={`task-list__header ${type === WorkRequestType.Inspection ? "is-inspection" : "is-preventative"}`}>
                    {type === WorkRequestType.Inspection ? (
                        <>
                            <span>Pass</span>
                            <span>Fail</span>
                            <span>Not Performed</span>
                            <span>N/A</span>
                            <span></span>
                            <span>Measurement</span>
                        </>
                    ) : (
                        <>
                            <span>Completed</span>
                            <span>Not Performed</span>
                            <span>N/A</span>
                            <span></span>
                            <span>Measurement</span>
                        </>
                    )}
                </div>

                {/* Check All row */}
                {type === WorkRequestType.Inspection ? (
                    <div className={`task-item task-list__select-all is-inspection`}>
                        <input
                            type="checkbox"
                            aria-label="Check all Pass"
                            checked={allAre(InspectionTaskValue.Pass)}
                            onChange={() => handleCheckAll(InspectionTaskValue.Pass)}
                        />
                        <input
                            type="checkbox"
                            aria-label="Check all Fail"
                            checked={allAre(InspectionTaskValue.Fail)}
                            onChange={() => handleCheckAll(InspectionTaskValue.Fail)}
                        />
                        <input
                            type="checkbox"
                            aria-label="Check all Not Performed"
                            checked={allAre(InspectionTaskValue.NotPerformed)}
                            onChange={() => handleCheckAll(InspectionTaskValue.NotPerformed)}
                        />
                        <input
                            type="checkbox"
                            aria-label="Check all N/A"
                            checked={allAre(InspectionTaskValue.NA)}
                            onChange={() => handleCheckAll(InspectionTaskValue.NA)}
                        />
                        <span></span>
                        <span></span>
                    </div>
                ) : (
                    <div className={`task-item task-list__select-all is-preventative`}>
                        <input
                            type="checkbox"
                            aria-label="Check all Completed"
                            checked={allAre(CompletableTaskValue.Performed)}
                            onChange={() => handleCheckAll(CompletableTaskValue.Performed)}
                        />
                        <input
                            type="checkbox"
                            aria-label="Check all Not Performed"
                            checked={allAre(CompletableTaskValue.NotPerformed)}
                            onChange={() => handleCheckAll(CompletableTaskValue.NotPerformed)}
                        />
                        <input
                            type="checkbox"
                            aria-label="Check all N/A"
                            checked={allAre(CompletableTaskValue.NA)}
                            onChange={() => handleCheckAll(CompletableTaskValue.NA)}
                        />
                        <span></span>
                        <span></span>
                    </div>
                )}

                {/* Rows */}
                {tasks.map((task) => (
                    <div key={task.id} className={`task-item ${type === WorkRequestType.Inspection ? "is-inspection" : "is-preventative"}`}>
                        {type === WorkRequestType.Inspection ? (
                            <>
                                <input
                                    type="checkbox"
                                    checked={task.value === InspectionTaskValue.Pass}
                                    onChange={() => handleToggle(task.id, InspectionTaskValue.Pass)}
                                />
                                <input
                                    type="checkbox"
                                    checked={task.value === InspectionTaskValue.Fail}
                                    onChange={() => handleToggle(task.id, InspectionTaskValue.Fail)}
                                />
                                <input
                                    type="checkbox"
                                    checked={task.value === InspectionTaskValue.NotPerformed}
                                    onChange={() => handleToggle(task.id, InspectionTaskValue.NotPerformed)}
                                />
                                <input
                                    type="checkbox"
                                    checked={task.value === InspectionTaskValue.NA}
                                    onChange={() => handleToggle(task.id, InspectionTaskValue.NA)}
                                />
                                <div className="task-item__text">
                                    <p className="task-item__label">{task.label}</p>
                                    <p className="task-item__description">{task.description}</p>
                                </div>
                                <input
                                    type="text"
                                    value={task.measurement || ""}
                                    placeholder="Measurement"
                                    onChange={(e) => handleMeasurementChange(task.id, e.target.value)}
                                    className="task-measurement"
                                />
                            </>
                        ) : (
                            <>
                                <input
                                    type="checkbox"
                                    checked={task.value === CompletableTaskValue.Performed}
                                    onChange={() => handleToggle(task.id, CompletableTaskValue.Performed)}
                                />
                                <input
                                    type="checkbox"
                                    checked={task.value === CompletableTaskValue.NotPerformed}
                                    onChange={() => handleToggle(task.id, CompletableTaskValue.NotPerformed)}
                                />
                                <input
                                    type="checkbox"
                                    checked={task.value === CompletableTaskValue.NA}
                                    onChange={() => handleToggle(task.id, CompletableTaskValue.NA)}
                                />
                                <div className="task-item__text">
                                    <p className="task-item__label">{task.label}</p>
                                        <p className="task-item__description">{task.description}</p>
                                </div>
                                <input
                                    type="text"
                                    value={task.measurement || ""}
                                    onChange={(e) => handleMeasurementChange(task.id, e.target.value)}
                                    className="task-measurement"
                                />
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default TaskList